import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { CheckCircle2, Clock, Coffee, DollarSign, Calendar } from "lucide-react";

export default function BenefitsModal({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="text-2xl" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            BENEFICII ȘI ÎNDATORIRI
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[60vh] pr-4">
          <div className="space-y-6">
            {/* Beneficii */}
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center text-green-700">
                <CheckCircle2 className="w-6 h-6 mr-2" />
                BENEFICII
              </h3>
              
              <div className="space-y-4">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-lg mb-2 flex items-center">
                    <Coffee className="w-5 h-5 mr-2" />
                    Pauze Flexible
                  </h4>
                  <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                    <li>Poți solicita până la <strong>2 pauze</strong> pe zi</li>
                    <li>Dacă administratorul nu răspunde în <strong>15 minute</strong>, pauza este aprobată automat</li>
                    <li>Pauza standard de masă: 12:00-13:00 (1 oră)</li>
                  </ul>
                </div>

                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-lg mb-2 flex items-center">
                    <Clock className="w-5 h-5 mr-2" />
                    Ore Compensatorii ("Ore Câștigate")
                  </h4>
                  <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                    <li>Dacă <strong>amâni pauza de masă</strong>, câștigi ore libere</li>
                    <li>Amânare <strong>jumătate</strong> (30 min) = <strong>0.5 ore</strong> compensatorii</li>
                    <li>Amânare <strong>completă</strong> (1 oră) = <strong>1 oră</strong> compensatorie</li>
                    <li>Aceste ore pot fi folosite pentru zile libere (cu aprobare administrator)</li>
                    <li>Urmărește contorul tău de ore compensatorii în dashboard</li>
                  </ul>
                </div>

                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <h4 className="font-semibold text-lg mb-2 flex items-center">
                    <DollarSign className="w-5 h-5 mr-2" />
                    Ore Suplimentare
                  </h4>
                  <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                    <li>Poți activa opțiunea <strong>"DORESC ORE SUPLIMENTARE"</strong></li>
                    <li>Orele lucrate după <strong>16:00</strong> se contorizează automat</li>
                    <li>La datele de <strong>15</strong> și <strong>31</strong> ale lunii, administratorul decide:</li>
                    <li className="ml-6">→ Plată cu <strong>1.5x</strong> tarif</li>
                    <li className="ml-6">→ SAU zile libere (8 ore = 1 zi liberă)</li>
                  </ul>
                </div>

                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <h4 className="font-semibold text-lg mb-2 flex items-center">
                    <Calendar className="w-5 h-5 mr-2" />
                    Pontaj Automat
                  </h4>
                  <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                    <li>Dacă faci parte dintr-o echipă predefinită pe un proiect și <strong>uiți să te pontezi</strong>:</li>
                    <li>Sistemul creează automat pontaj <strong>7:00-16:00</strong> (sau programul proiectului)</li>
                    <li>La intrarea în aplicație, vei primi mesaj să confirmi/editezi ora</li>
                    <li>Poți edita ora exactă la care ai ajuns efectiv</li>
                  </ul>
                </div>
              </div>
            </div>

            <Separator className="my-6" />

            {/* Îndatoriri */}
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center text-red-700">
                <CheckCircle2 className="w-6 h-6 mr-2" />
                ÎNDATORIRI
              </h3>
              
              <div className="space-y-4">
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h4 className="font-semibold text-lg mb-2">Program Standard</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                    <li>Prezență obligatorie: <strong>7:00 - 16:00</strong></li>
                    <li>Pauză masă: <strong>12:00 - 13:00</strong> (1 oră)</li>
                    <li>Excepție: Proiecte speciale pot avea program diferit</li>
                  </ul>
                </div>

                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h4 className="font-semibold text-lg mb-2">Pontaj Obligatoriu</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                    <li>Trebuie să dai <strong>PREZENT/SOSIT</strong> la sosire</li>
                    <li>Trebuie să dai <strong>ÎNCHEIERE PROGRAM</strong> la plecare</li>
                    <li>Selectează șantierul corect și uneltele folosite</li>
                    <li>Permite accesul la locație pentru pontaj valid</li>
                  </ul>
                </div>

                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h4 className="font-semibold text-lg mb-2">Pauze</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                    <li>Maximum <strong>2 pauze</strong> pe zi (în afara pauzei de masă)</li>
                    <li>Pauzele trebuie solicitate din aplicație</li>
                    <li>Specificare motiv și durată</li>
                  </ul>
                </div>

                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h4 className="font-semibold text-lg mb-2">Ore Suplimentare</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                    <li>Trebuie să activezi opțiunea înainte</li>
                    <li>Orele suplimentare se fac doar cu aprobare prealabilă</li>
                    <li>Trebuie să dai <strong>ÎNCHEIERE PROGRAM</strong> când pleci</li>
                  </ul>
                </div>

                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-300">
                  <h4 className="font-semibold text-lg mb-2">⚠️ Important</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                    <li>Pontajul automat se marchează diferit în calendar</li>
                    <li>Administratorul vede locația GPS la fiecare pontaj</li>
                    <li>Utilizarea orelor compensatorii necesită aprobare</li>
                    <li>Decizia pentru orele suplimentare se ia la 15 și 31</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
